// AuthContext — identity, resolved once from /me.
//
// Authentik authenticates; TownForge authorizes (TD-004). A token gives us the subject; everything
// that decides what a user may do comes from /me, which composes it from our own control plane.
//
// Hydration failure is REPORTED, never papered over with a default identity (TD-034). An app that
// silently assumes a role would show an officer buttons they cannot use, or hide ones they can.
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { api, ApiError, type Me } from '../lib/api'
import type { Identity, OrgType, RoleKind } from '../lib/capabilities'

interface AuthState {
  me: Me | null
  identity: Identity
  loading: boolean
  error: string | null
  reload: () => void
}

const ANONYMOUS: Identity = {
  role: null, orgType: null, features: [], isSuperuser: false, authEnabled: true,
}

const Ctx = createContext<AuthState>({
  me: null, identity: ANONYMOUS, loading: true, error: null, reload: () => {},
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api.me()
      .then(r => { if (!cancelled) { setMe(r); setError(null) } })
      .catch((e: unknown) => {
        if (cancelled) return
        setMe(null)
        setError(e instanceof ApiError
          ? (e.status === 401 ? 'Not signed in.' : `Could not load your profile: ${e.detail}`)
          : 'Could not reach TownForge.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [nonce])

  const identity: Identity = me
    ? {
        role: (me.role as RoleKind) ?? null,
        orgType: (me.org?.orgType as OrgType) ?? null,
        features: me.entitlements.features,
        isSuperuser: me.user.isSuperuser,
        authEnabled: me.authEnabled,
      }
    : ANONYMOUS

  return (
    <Ctx.Provider value={{ me, identity, loading, error, reload: () => setNonce(n => n + 1) }}>
      {children}
    </Ctx.Provider>
  )
}

export function useAuth() {
  return useContext(Ctx)
}
